import moment from 'moment';
import { create } from 'zustand';
import AppService from '../services/AppService';
import NewCustomerForm from './NewCustomerForm';
import * as StoreHelper from './StoreHelper';

import { IAvailableVerifications, ICheckOrderResponse, IInitialValues, INewCustomerForm, IPlan, IStepsTypes, IVerificationType, StepsStore } from '../typings';
import { validateBillingDetails, validateEmergencyContacts, validateNewSubscriptionDetails, validateVATDetails } from './Validations';

const appsService = new AppService((window as any).shop_domain);

let gclFlowCompleted = window.location.search?.indexOf('gcl=true') > -1 ? true : false;
let deviceId = '';
let initialStep: IStepsTypes = 'deviceId';

let initialValues: IInitialValues | null = localStorage.getItem('gcl_request') ? JSON.parse(localStorage.getItem('gcl_request') ?? '') : null;

const indexOfDeviceId = window.location.search?.indexOf('deviceId=');
if (indexOfDeviceId > -1) {
    deviceId = window.location.search?.substring(indexOfDeviceId + 'deviceId='.length);
}
if (deviceId && initialValues?.deviceId && initialValues.deviceId !== deviceId) {
    initialValues = null;
    localStorage.removeItem('gcl_request');
}

let initialCustomerForm = NewCustomerForm.getNewCustomerForm();

if (initialValues && StoreHelper.isAfterOrderVerificationStep(initialValues.currentStep, initialValues.choosenSteps)) {
    if (initialValues.orderId) {
        if (gclFlowCompleted) {
            initialCustomerForm = StoreHelper.getNCFFromStorage();
            initialStep = initialValues.gcl_confirmed ? 'final' : 'after_gcl';
        } else {
            initialStep = 'verifyOrder';
        }
    }
}

export const useStepsStore = create<StepsStore>((set, get) => ({
    userDetailsErrors: [],
    emergencyDetailsErrors: [],
    gclErrors: [],
    vatErrors: [],
    no_vat_plans: [],
    one_month_plans: [],
    execution: 0,
    annual_discounts: { vat: 0, no_vat: 0 },
    subscription_starts_at: initialValues?.subscription_starts_at ?? '',
    gcl_confirmed: initialValues?.gcl_confirmed ?? false,
    vat_plans: [],
    order_info: { zip: '', items: [{ image_url: '', price: '', quantity: 0, title: '', desc: '', variant_id: 0 }], total_price: '40' },
    selectedVariantID: initialValues?.selectedVariantId ?? null,
    selectedPlanTitle: initialValues?.selectedPlanTitle ?? '',
    titleOptions: ['Mr', 'Ms', 'Mrs', 'Miss', 'Dr', 'Pro', 'Rev'] as any,
    newCustomerForm: initialCustomerForm,
    currentStep: initialStep,
    savedStep: initialValues?.currentStep && StoreHelper.isAfterOrderVerificationStep(initialValues?.currentStep, initialValues.choosenSteps) ? initialValues?.currentStep : null,
    secureHash: initialValues?.hash ?? null,
    deviceId: initialValues?.deviceId ? initialValues?.deviceId : (deviceId ?? null),
    available_verifications: initialValues?.available_verifications ?? { email: { available: false, bit: '' }, zip: { available: false, bit: '' }, phone: { available: false, bit: '' }, source: { available: false, bit: '' } },
    orderId: initialValues?.orderId ?? null,
    gclFlowCompleted: gclFlowCompleted && initialValues?.flow ? true : false,
    flow: initialValues?.flow ?? '',
    flowId: initialValues?.flowId ?? '',
    bitUsedRepeat: '',
    accountNumber: initialValues?.accountNumber ?? '',
    sortCode: initialValues?.sortCode ?? '',
    error: '',
    carelineServiceStartsAt: initialValues?.carelineServiceStartsAt ?? null,
    stepAttempts: 0,
    subscriptionPrice: initialValues?.subscription_price ?? null,
    verificationType: 'email',
    steps: [],
    iAcceptToShareVatInfo: false,
    setCarelineServiceStartsAt: (date: string | null) => {
        set(() => ({ carelineServiceStartsAt: date ? moment(date).toDate() : null }));
    },
    setIAcceptToShareVatInfo: (iAcceptToShareVatInfo: boolean) => {
        set(() => ({ iAcceptToShareVatInfo: iAcceptToShareVatInfo }));
    },
    clearError: () => {
        set(() => ({ error: '' }));
    },
    setSteps: (choosenSteps: IStepsTypes[]) => {
        set(() => ({ steps: choosenSteps }))
    },
    setSelectedVariantId: (variantId: number | null) => {
        set(() => ({ selectedVariantID: variantId }));
    },
    setSelectedPlanTitle: (variantTitle: string | null) => {
        set(() => ({ selectedPlanTitle: variantTitle ?? '' }));
    },
    setSubscriptionPrice: (subscriptionPrice: string | number | null) => {
        set(() => ({ subscriptionPrice: subscriptionPrice }));
    },
    setFormValue: (entry: string, fields: (string | number)[], newValue: string | boolean | object) => {
        if (!fields?.length) {
            (get().newCustomerForm as any)[entry] = newValue;
        } else if (fields.length === 1) {
            (get().newCustomerForm as any)[entry][fields[0]] = newValue;
        } else if (fields.length === 2) {
            if (typeof fields[1] === 'number') {
                if ((get().newCustomerForm as any)[entry][fields[0]][fields[1]]) {
                    (get().newCustomerForm as any)[entry][fields[0]][fields[1]] = newValue;
                } else {
                    (get().newCustomerForm as any)[entry][fields[0]].push(newValue);
                }
            } else {
                (get().newCustomerForm as any)[entry][fields[0]][fields[1]] = newValue;
            }
        } else if (fields.length === 3) {
            (get().newCustomerForm as any)[entry][fields[0]][fields[1]][fields[2]] = newValue;
        }
    },
    setEmergencyContacts: (num: number) => {
        if (num === 0) {
            get().newCustomerForm.emergency_contacts = [];
        } else {
            if (get().newCustomerForm.emergency_contacts && get().newCustomerForm.emergency_contacts?.length > num) {
                get().newCustomerForm.emergency_contacts.splice(num);
            } else {
                const currentNum = get().newCustomerForm.emergency_contacts?.length ?? 0;
                get().newCustomerForm.emergency_contacts.push(...[...Array(num - currentNum)].map(() => NewCustomerForm.getEmergencyContact()))
            }
        }
    },
    setVerificationType: (verificationType: IVerificationType) => {
        set((state) => ({ verificationType: verificationType }));
    },
    setDeviceId: (deviceId: string) => {
        set((state) => ({ deviceId: deviceId }));
    },
    setAccountNumber: (accountNumber: string) => {
        set((state) => ({ accountNumber: accountNumber }));
    },
    setSortCode: (sortCode: string) => {
        set((state) => ({ sortCode: sortCode }));
    },
    goToVerifyStep: () => {
        set(() => ({ currentStep: 'verifyOrder' }));
    },
    resetStore: () => {
        set(() => ({ subscriptionPrice: null, carelineServiceStartsAt: null, gcl_confirmed: false, selectedVariantID: null, subscription_starts_at: '', savedStep: null, secureHash: '', flow: '', flowId: '' }));
    },
    nextStep: () => {
        if (get().currentStep === 'deviceId') {
            if (!get().deviceId) {
                set(() => ({ error: 'Please enter your activation code to continue', execution: get().execution + 1 }));
            } else {
                if (get().deviceId !== initialValues?.deviceId) {
                    window.localStorage.removeItem('gcl_request');
                    get().resetStore();
                }
                const error_message_final = "Sorry, we still can't recognise your code. Please call our team on <a href='tel:+448083043719'>0808 304 3719</a> to help with this.";
                appsService.getOrderByDeviceId(get().deviceId ?? '').then((response) => {
                    const availableVerifications: IAvailableVerifications = { zip: { available: false, bit: '' }, email: { available: false, bit: '' }, phone: { available: false, bit: '' }, source: { available: false, bit: '' } };
                    if (response.email) {
                        availableVerifications.email.available = true;
                        availableVerifications.email.bit = `@${response.email}`;
                    }
                    if (response.phone) {
                        availableVerifications.phone.available = true;
                        availableVerifications.phone.bit = response.phone;
                    }
                    if (response.source_identifier) {
                        availableVerifications.source.available = true;
                        availableVerifications.source.bit = response.source_identifier;
                    }
                    if (response.zip) {
                        availableVerifications.zip.available = true;
                        availableVerifications.zip.bit = response.zip;
                    }
                    StoreHelper.clearStorage();
                    set(() => ({ execution: get().execution + 1, orderId: response.order_id, currentStep: response.order_id ? 'verifyOrder' : 'deviceId', available_verifications: availableVerifications, stepAttempts: response.order_id ? 0 : get().stepAttempts + 1, error: response.order_id ? '' : (get().stepAttempts > 0 ? error_message_final : `We could not find your order, please check your activation code and try again`) }));
                }).catch(e => {
                    set(() => ({ execution: get().execution + 1, stepAttempts: get().stepAttempts + 1, error: (get().stepAttempts > 0 ? error_message_final : `We could not find your order, please check your activation code and try again`) }));
                })
            }
        } else if (get().currentStep === 'verifyOrder') {
            let bit: string = get().bitUsedRepeat ?? '';

            if (get().deviceId && bit) {
                appsService.checkOrderByDeviceId(get().deviceId ?? '', bit, get().verificationType).then((res: ICheckOrderResponse) => {
                    if (res.ok) {
                        let subscriptionStartsAt = '';
                        let subscriptionPrice: number | string | null = '';
                        if (res.created_at) {
                            subscriptionStartsAt = moment(res.created_at).add(1, 'months').startOf('day').format('Do MMMM YYYY');
                        }
                        const ncf: INewCustomerForm = res.ncf ? JSON.parse(res.ncf) : NewCustomerForm.getNewCustomerForm(res.billing_info ?? null, res.shipping_info ?? null);
                        if (!ncf.billing_info.address?.line1 && res.billing_info) {
                            ncf.billing_info = res.billing_info;
                        }
                        if (!ncf.shipping_info.address?.line1 && res.shipping_info) {
                            ncf.shipping_info = res.shipping_info;
                        }
                        const vatPlans: IPlan[] = StoreHelper.getPlansFromProduct(res.product?.vat);
                        const noVatPlans: IPlan[] = StoreHelper.getPlansFromProduct(res.product?.no_vat);
                        const oneMonthPlans = StoreHelper.getOneMonthPlans(res.product.vat, res.product.no_vat);
                        const annualDiscounts = StoreHelper.getAnnualDiscounts(res.product);
                        if (res.next_order?.variant_id) {
                            const plans = [...res.product?.vat.variants, ...res.product?.no_vat.variants];
                            subscriptionPrice = plans.find(v => v.id === res.next_order?.variant_id)?.price ?? null;
                        }
                        let nextStep = get().savedStep ?? StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps);
                        if (res.is_gcl_completed) {
                            nextStep = 'final';
                        }
                        set(() => ({
                            stepAttempts: 0, annual_discounts: annualDiscounts, subscriptionPrice: subscriptionPrice,
                            one_month_plans: oneMonthPlans, subscription_starts_at: subscriptionStartsAt, order_info: res.order_info,
                            currentStep: nextStep, newCustomerForm: ncf, secureHash: res.hash, error: '', vat_plans: vatPlans, no_vat_plans: noVatPlans
                        }));
                        StoreHelper.saveToStorage(get(), res.hash);
                    } else {
                        if (get().bitUsedRepeat && get().bitUsedRepeat?.trim() !== '') {
                            const error_message_final = "Sorry, we still could not find your order. Please call our team on <a href='tel:+448083043719'>0808 304 3719</a> to help with this.";
                            const error = get().stepAttempts === 0 ? 'We could not find your order. Please try again.' : error_message_final;
                            set(() => ({ stepAttempts: get().stepAttempts + 1, error: error, execution: get().execution + 1 }));
                        } else {
                            set(() => ({ stepAttempts: get().stepAttempts + 1, error: 'Please enter details to verify your purchase', execution: get().execution + 1 }));
                        }
                    }
                });
            } else {
                set(() => ({ error: 'Please enter details to verify your purchase', execution: get().execution + 1 }));
            }
        } else if (get().currentStep === 'showOrder') {
            set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps), error: '' }));
        } else if (get().currentStep === 'ncf_userDetails') {
            const userDetailsErrors: string[] = [];
            set(() => ({ userDetailsErrors: userDetailsErrors, emergencyDetailsErrors: [] }));
            if (get().deviceId && get().orderId && userDetailsErrors.length === 0) {
                appsService.saveNewCustomerFlowForm(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm).then(() => {
                    set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps), error: '' }));
                    StoreHelper.saveToStorage(get(), get().secureHash);
                });
            } else {
                set(() => ({ error: userDetailsErrors?.length === 0 ? 'Something went wrong. Please validate your device again!' : 'You need to fill all required fields' }));
            }
        } else if (get().currentStep === 'ncf_medicalDetails') {
            set(() => ({ userDetailsErrors: [], emergencyDetailsErrors: [] }));
            appsService.saveNewCustomerFlowForm(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm).then(() => {
                set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps), error: '' }));
                StoreHelper.saveToStorage(get(), get().secureHash);
            });
        } else if (get().currentStep === 'ncf_emergency') {
            const emergencyDetailsErrors: { num: number; errors: string[] }[] = [];
            emergencyDetailsErrors.push(...validateEmergencyContacts(get().newCustomerForm.emergency_contacts));
            set(() => ({ emergencyDetailsErrors: emergencyDetailsErrors, step5ErrorFields: [] }));
            if (get().deviceId && get().orderId && emergencyDetailsErrors.length === 0) {
                appsService.saveNewCustomerFlowForm(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm).then(() => {
                    set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps), error: '' }));
                    StoreHelper.saveToStorage(get(), get().secureHash);
                });
            } else {
                set(() => ({ error: emergencyDetailsErrors?.length === 0 ? 'Something went wrong. Please validate your device again!' : 'You need to fill all required fields' }));
            }
        } else if (get().currentStep === 'billing_details') {
            const gclErrors: string[] = [];
            gclErrors.push(...validateBillingDetails(get().newCustomerForm));
            if (gclErrors?.length === 0 || (gclErrors.length === 1 && gclErrors[0] === 'email')) {
                appsService.saveNewCustomerFlowForm(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm)
                    .then(() => {
                        let nextStep = StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps);
                        if (!get().newCustomerForm.vat_exempt && nextStep === 'vat_details') {
                            nextStep = StoreHelper.getNextRegistrationFlowStep(nextStep, get().steps);
                        }
                        set(() => ({ currentStep: nextStep, error: '' }));
                        StoreHelper.saveToStorage(get(), get().secureHash);

                        appsService.saveServiceStartsAt(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().carelineServiceStartsAt);
                    });
            } else {
                set(() => ({ error: 'Your details are not complete', gclErrors: gclErrors }));
            }
        } else if (get().currentStep === 'vat_details') {
            const gclErrors: string[] = [];
            const vatErrors: string[] = [];
            gclErrors.push(...validateBillingDetails(get().newCustomerForm));
            vatErrors.push(...validateVATDetails(get()));
            if (gclErrors?.length === 0 && vatErrors?.length === 0) {
                appsService.saveNewCustomerFlowForm(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm).then(() => {
                    set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps), error: '' }));
                    StoreHelper.saveToStorage(get(), get().secureHash);
                });
            } else {
                if (gclErrors.length > 0) {
                    set(() => ({ error: 'Your details are not complete', gclErrors: gclErrors }));
                }
                if (vatErrors.length > 0) {
                    set(() => ({ error: 'To be VAT exempt, please select the medical conditions that apply and confirm you are happy to share this information.', gclErrors: gclErrors }));
                }
            }
        } else if (get().currentStep === 'gcl') {
            const gclErrors: string[] = [];
            if (!get().selectedVariantID) {
                gclErrors.push('plan');
                set(() => ({ error: 'You must select a plan' }));
            }
            gclErrors.push(...validateNewSubscriptionDetails(get()));
            if (gclErrors?.length === 0) {
                const orderId = get().orderId;
                if (!get().flow && orderId) {
                    appsService.getBillingFlow({
                        orderId: orderId, city: get().newCustomerForm.billing_info.address.city, zip: get().newCustomerForm.billing_info.address.zip,
                        account: get().accountNumber, sortcode: get().sortCode,
                        email: get().newCustomerForm.billing_info.email, line1: get().newCustomerForm.billing_info.address.line1,
                        name: get().newCustomerForm.billing_info.first_name, surname: get().newCustomerForm.billing_info.last_name
                    }, get().newCustomerForm, get().deviceId ?? '', get().secureHash ?? '', get().selectedVariantID ?? null).then((response) => {
                        if (response.url) {
                             const plans = [...get().no_vat_plans, ...get().vat_plans];
                            const planTitle = plans.find(p => p.id === get().selectedVariantID)?.title ?? '';
                            get().setSelectedPlanTitle(planTitle);
                            StoreHelper.saveToStorage(get(), get().secureHash);
                            setTimeout(() => { window.location.href = response.url; }, 1);
                        } else if (response.msg) {
                            set(() => ({ error: 'We have not been able to verify your bank account. Make sure the bank account details are correct or contact us at info@careline365.co.uk' }));
                        } else {
                            set(() => ({ error: 'Something went wrong!. Try again in a few minutes or contact us at info@careline365.co.uk' }));
                        }
                    }).catch(() => {
                        set(() => ({ error: 'Something went wrong!. Try again in a few minutes or contact us at info@careline365.co.uk' }));
                    });
                }
                if (get().flow && get().gclFlowCompleted) {
                    set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps) }));
                } else if (get().flow && !get().gclFlowCompleted) {
                    window.location.href = get().flow;
                }
            } else {
                set(() => ({ error: 'The form is not complete', gclErrors: gclErrors }));
            }
        } else if (get().currentStep === 'after_gcl') {
            appsService.saveGCLConfirmation(get().deviceId ?? '', get().orderId as number, get().secureHash ?? '', get().newCustomerForm).then(() => {
                get().gcl_confirmed = true;
                StoreHelper.saveToStorage(get(), get().secureHash);
                set(() => ({ currentStep: StoreHelper.getNextRegistrationFlowStep(get().currentStep, get().steps) }));
            }).catch(() => {
                set(() => ({ currentStep: 'gcl', error: 'We could not verify that you set up GoCardless. Try again or contact us at info@careline365.co.uk' }));
            });
        } else if (get().currentStep === 'final') {

        }
    },
    previousStep: () => {
        let previousStep = StoreHelper.getPreviousRegistrationFlowStep(get().currentStep, get().steps);
        if (previousStep === 'vat_details' && !get().newCustomerForm.vat_exempt) {
            previousStep = StoreHelper.getPreviousRegistrationFlowStep(previousStep, get().steps);
        }
        set((state) => ({ currentStep: previousStep }));
    },
    reset: () => {
        set((state) => ({ currentStep: 'deviceId' }));
    },
    setVerificationBitUsedRepeat: (bitUsedRepeat: string) => {
        set((state) => ({ bitUsedRepeat: bitUsedRepeat }));
    }
}));